import React, { useState, createContext, useContext } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaXmark, FaRightFromBracket, FaMagnifyingGlass, FaBell, FaGlobe,
  FaHouseChimney, FaUserGear, FaUsers, FaBoxesStacked, FaClipboardCheck, FaHandshake,
  FaMoneyBillWave, FaSackDollar, FaGear, FaFileExcel, FaHelmetSafety, FaHandHoldingDollar, FaWallet } from 'react-icons/fa6';
import { useAuth } from './auth';
import { Avatar } from './ui';

// Language context — Bangla/English toggle
const LangContext = createContext({ lang: 'bn', toggle: () => {} });
export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('cms_lang') || 'bn');
  const toggle = () => {
    const next = lang === 'bn' ? 'en' : 'bn';
    setLang(next);
    localStorage.setItem('cms_lang', next);
  };
  return <LangContext.Provider value={{ lang, toggle }}>{children}</LangContext.Provider>;
}
export function useLang() { return useContext(LangContext); }

// Translation dictionary
const T = {
  bn: {
    overview: 'ওভারভিউ', contractors: 'কন্ট্রাক্টর', workers: 'কর্মীরা', stock: 'মাল স্টক',
    attendance: 'হাজিরা', requests: 'মাল অনুরোধ', expenses: 'খরচ', salaries: 'বেতন',
    project: 'প্রজেক্ট সেটিংস', logout: 'লগআউট', search: 'ওয়ার্কার, স্টক, হাজিরা খুঁজুন',
    backup: 'ব্যাকআপ/সেটিংস',
    myWorkers: 'আমার কর্মীরা', myStock: 'আমার মাল', myBill: 'আমার বিল', requestMaterial: 'মাল চাই',
    myAttendance: 'আমার হাজিরা', mySalary: 'আমার বেতন', myAdvances: 'আমার অগ্রিম',
  },
  en: {
    overview: 'Overview', contractors: 'Contractors', workers: 'Workers', stock: 'Stock',
    attendance: 'Attendance', requests: 'Requests', expenses: 'Expenses', salaries: 'Salaries',
    project: 'Project Settings', logout: 'Logout', search: 'Search workers, stock, attendance',
    backup: 'Backup / Settings',
    myWorkers: 'My Workers', myStock: 'My Stock', myBill: 'My Bill', requestMaterial: 'Request Material',
    myAttendance: 'My Attendance', mySalary: 'My Salary', myAdvances: 'My Advances',
  },
};

export default function Layout({ children }) {
  const { profile, logout } = useAuth();
  const { lang, toggle } = useLang();
  const t = T[lang];
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const role = profile?.user?.role;
  const project = profile?.project;

  const links = {
    owner: [
      { to: '/', label: t.overview, icon: FaHouseChimney },
      { to: '/contractors', label: t.contractors, icon: FaUserGear },
      { to: '/workers', label: t.workers, icon: FaUsers },
      { to: '/stock', label: t.stock, icon: FaBoxesStacked },
      { to: '/attendance', label: t.attendance, icon: FaClipboardCheck },
      { to: '/requests', label: t.requests, icon: FaHandshake },
      { to: '/expenses', label: t.expenses, icon: FaMoneyBillWave },
      { to: '/salaries', label: t.salaries, icon: FaSackDollar },
      { to: '/settings', label: t.backup, icon: FaFileExcel },
      { to: '/project', label: t.project, icon: FaGear },
    ],
    manager: [
      { to: '/', label: t.overview, icon: FaHouseChimney },
      { to: '/contractors', label: t.contractors, icon: FaUserGear },
      { to: '/workers', label: t.workers, icon: FaUsers },
      { to: '/stock', label: t.stock, icon: FaBoxesStacked },
      { to: '/attendance', label: t.attendance, icon: FaClipboardCheck },
      { to: '/requests', label: t.requests, icon: FaHandshake },
      { to: '/settings', label: t.backup, icon: FaFileExcel },
    ],
    contractor: [
      { to: '/', label: t.overview, icon: FaHouseChimney },
      { to: '/workers', label: t.myWorkers, icon: FaUserGear },
      { to: '/stock', label: t.myStock, icon: FaBoxesStacked },
      { to: '/requests', label: t.requestMaterial, icon: FaHandshake },
      { to: '/attendance', label: t.myAttendance, icon: FaClipboardCheck },
      { to: '/bill', label: t.myBill, icon: FaSackDollar },
    ],
    worker: [
      { to: '/', label: t.overview, icon: FaHouseChimney },
      { to: '/attendance', label: t.myAttendance, icon: FaClipboardCheck },
      { to: '/salary', label: t.mySalary, icon: FaWallet },
      { to: '/advances', label: t.myAdvances, icon: FaHandHoldingDollar },
    ],
  };
  const items = links[role] || [];
  const tabs = items.slice(0, 4);

  const NavLinkItem = ({ l, onNavigate }) => (
    <NavLink key={l.to} to={l.to} end={l.to === '/'} onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-all ${isActive
          ? 'text-brand-400 font-semibold bg-white/10 ring-1 ring-white/10'
          : 'text-ink-300 hover:text-white hover:bg-white/5'}`}>
      <l.icon size={16} className="shrink-0" /> {l.label}
    </NavLink>
  );

  const BrandMark = (
    <div className={`rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-lg font-bold shadow-sm ${role === 'owner' ? '' : ''}`}>
      <FaHelmetSafety size="1.1em" />
    </div>
  );

  return (
    <div className="min-h-screen bg-page">
      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-40 lg:hidden ${open ? '' : 'pointer-events-none'}`}>
        <div className={`absolute inset-0 bg-ink-900/50 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`} onClick={() => setOpen(false)} />
        <div className={`absolute left-0 top-0 bottom-0 w-72 bg-ink-900 shadow-2xl transition-transform duration-300 ease-out overflow-y-auto flex flex-col ${open ? 'translate-x-0' : '-translate-x-full'}`}>
          <button onClick={() => setOpen(false)} className="absolute top-4 right-4 p-1.5 rounded-lg text-ink-400 hover:bg-white/10 z-10"><FaXmark /></button>
          <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-lg font-bold"><FaHelmetSafety size="1em" /></div>
            <div>
              <div className="font-semibold text-white text-sm">{project?.name || 'Construction MS'}</div>
              <div className="text-xs text-ink-400 capitalize">{role}</div>
            </div>
          </div>
          <nav className="flex-1 px-3 py-3 space-y-0.5">
            {items.map((l) => <NavLinkItem key={l.to} l={l} onNavigate={() => setOpen(false)} />)}
          </nav>
          <div className="px-3 pb-6">
            <button onClick={toggle} className="w-full flex items-center gap-3 px-3 py-2 text-xs text-ink-400 hover:text-white transition rounded-lg">
              <FaGlobe /> {lang === 'bn' ? 'English' : 'বাংলা'}
            </button>
            <button onClick={() => { logout(); navigate('/login'); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-ink-400 hover:text-rose-400 hover:bg-white/5 rounded-lg transition mt-2">
              <FaRightFromBracket /> {t.logout}
            </button>
            <div className="mt-3 p-3 bg-white/5 rounded-xl">
              <div className="text-xs font-semibold text-brand-400">🏗️ {lang === 'bn' ? 'পেশাদার নির্মাণ ব্যবস্থাপনা' : 'Professional Construction Management'}</div>
              <p className="text-xs text-ink-500 mt-1 leading-relaxed">{lang === 'bn' ? 'প্রতিটি কন্ট্রাক্টরের জন্য আলাদা স্টক, হাজিরা ও রিপোর্ট।' : 'Separate stock, attendance & reports for every contractor side.'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-ink-100 px-4 lg:px-6 py-2.5 flex items-center gap-3">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-ink-50 text-ink-500 lg:hidden"><FaBars /></button>
        <div className="hidden lg:flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center text-base font-bold shadow-sm"><FaHelmetSafety /></div>
          <span className="font-bold text-ink-900 text-[15px]">{project?.name || 'Construction MS'}</span>
          <span className="text-xs text-ink-400 font-medium hidden xl:block">• নির্মাণ ব্যবস্থাপনা</span>
        </div>

        <div className="hidden md:block flex-1 max-w-md mx-auto lg:mx-4">
          <div className="relative">
            <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 text-xs" />
            <input className="w-full pl-9 pr-3 py-2 rounded-xl bg-ink-50 border border-ink-100 text-sm placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-500" placeholder={t.search} />
          </div>
        </div>

        <div className="flex items-center gap-1.5 ml-auto">
          <button onClick={toggle} className="p-2 text-ink-400 hover:text-ink-700 rounded-lg hover:bg-ink-50" title="English/Bangla"><FaGlobe /></button>
          <button className="relative p-2 text-ink-400 hover:text-ink-700 rounded-lg hover:bg-ink-50"><FaBell /><span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-brand-500 ring-2 ring-white" /></button>
          <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-ink-100">
            <Avatar name={profile?.user?.name} className="w-8 h-8 text-xs" />
            <div className="hidden lg:block leading-tight">
              <div className="text-sm font-semibold text-ink-800 truncate max-w-[140px]">{profile?.user?.name}</div>
              <div className="text-[11px] text-ink-400 capitalize">{role}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-ink-900 text-ink-300 h-[calc(100vh-57px)] sticky top-[57px] px-3 py-4">
          <nav className="flex-1 space-y-0.5">
            {items.map((l) => <NavLinkItem key={l.to} l={l} />)}
          </nav>
          <div className="mt-auto space-y-1 pt-4 border-t border-white/10">
            <div className="p-3 bg-white/5 rounded-xl mb-1">
              <div className="text-xs font-semibold text-brand-400">🏗️ {lang === 'bn' ? 'পেশাদার নির্মাণ ব্যবস্থাপনা' : 'Professional Construction Management'}</div>
              <p className="text-[11px] text-ink-500 mt-1 leading-relaxed">{lang === 'bn' ? 'প্রতিটি কন্ট্রাক্টরের জন্য আলাদা স্টক, হাতাইটিস ও রিপোর্ট।' : 'Separate stock, attendance & reports for every contractor side.'}</p>
            </div>
            <button onClick={toggle} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-ink-300 hover:text-white hover:bg-white/5 rounded-lg transition">
              <FaGlobe className="w-4" /> {lang === 'bn' ? 'English' : 'বাংলা'}
            </button>
            <button onClick={() => { logout(); navigate('/login'); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-ink-400 hover:text-rose-400 hover:bg-white/5 rounded-lg transition">
              <FaRightFromBracket /> {t.logout}
            </button>
          </div>
        </aside>

        <main className="flex-1 w-full max-w-6xl mx-auto p-4 lg:p-6 pb-24 lg:pb-8 min-w-0">
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-ink-100 pb-safe">
        <div className="flex items-stretch">
          {tabs.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === '/'}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition ${isActive ? 'text-brand-600' : 'text-ink-400'}`}>
              {({ isActive }) => (
                <>
                  <span className={`rounded-lg px-3 py-1 ${isActive ? 'bg-brand-50' : ''}`}><l.icon size={16} /></span>
                  <span className="max-w-full truncate px-1">{l.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}